import React from 'react';
import ReactDOM from 'react-dom/client';
import { CoreWebWrapper, i18nCore } from '@pro4-erp/core-web';
import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SnackbarProvider } from 'notistack';
import { ConfirmProvider } from 'material-ui-confirm';
import { createTheme } from '@mui/material';
import * as Sentry from '@sentry/react';
import updateLocale from 'dayjs/plugin/updateLocale';
import dayjs from 'dayjs';
import { getInitialLanguage, UserImplementation } from './core';
import AuthenticatedLayout from './core/layouts/AuthenticatedLayout';
import {
  AuditorCompetenceCreatePage,
  AuditorCompetenceDetailPage,
  AuditorCompetencesBrowsePage,
} from './features/auditor-competences';
import { AuditorVisitDetailPage, AuditorVisitsBrowsePage } from './features/auditor-visits';
import UnauthenticatedLayout from './core/layouts/UnauthenticatedLayout';
import { RemindersBrowsePage } from './features/reminders';
import { FinishRegistrationPage } from './features/finish-registration';
import { UserSettingsPage } from './features/user-settings';
import {
  ValidAuditorRegistrationDetailPage,
  ValidAuditorRegistrationsBrowsePage,
} from './features/valid-auditor-registrations';
import { ClientVisitDetailPage, ClientVisitsBrowsePage } from './features/client-visits';
import {
  AuditInvitationDetailPage,
  AuditInvitationsBrowsePage,
} from './features/audit-invitations';
import {
  AuditorProposalsBrowsePage,
  AuditorProposalsCreatePage,
  AuditorProposalsDetailPage,
} from './features/audit-proposals';
import { ForgotPasswordPage } from './features/forgot-password';
import { LoginPage } from './features/login';
import { ResetPasswordPage } from './features/reset-password';
import { CertificateBrowsePage, CertificateDetailPage } from './features/client-certificates';
import { ClientWorkOrderBrowsePage, ClientWorkOrderDetailPage } from './features/client-workorder';
import { HomePage } from './features/client-landing';
import {
  AuditorVisitRepReviewBrowsePage,
  AuditorVisitRepReviewDetailPage,
} from './features/audit-visitrepreview';
import AuthTokenWrapper from './core/layouts/AuthTokenWrapper';
import { AuditGradePage } from './features/audit-survey';
import env from '@/env';
import { ClientGuard } from './core/layouts/ClientGuard';

Sentry.init({
  enabled: env.VITE_ENV === 'production' || env.VITE_ENV === 'staging',
  dsn: env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration({
      maskAllText: false,
    }),
    Sentry.httpClientIntegration(),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [env.VITE_API_URL],
  environment: env.VITE_ENV,
  // Session Replay
  replaysSessionSampleRate: env.VITE_ENV === 'development' ? 0 : 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: env.VITE_ENV === 'development' ? 0 : 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

dayjs.extend(updateLocale);
dayjs.updateLocale('en', {
  weekStart: 1,
});
const queryClient = new QueryClient();
const options = {
  theme: createTheme({
    palette: {
      text: {
        disabled: '#000000',
      },
    },
    components: {
      MuiOutlinedInput: {
        variants: [
          {
            props: { disabled: true },
            style: {
              background: '#a3a3a317',
            },
          },
        ],
      },
    },
  }),
};

// TODO move impl to zustand store
export const impl = new UserImplementation();

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(createBrowserRouter);

const router = sentryCreateBrowserRouter([
  {
    path: '/',
    element: <Outlet />,
    children: [
      {
        element: <AuthTokenWrapper />,
        children: [
          {
            path: 'auth/',
            element: <UnauthenticatedLayout />,
            children: [
              {
                path: 'finish-registration/:registrationUrlId',
                element: <FinishRegistrationPage />,
              },
              {
                path: 'forgot-password',
                element: <ForgotPasswordPage />,
              },
              {
                path: 'reset-password/:recoverHash',
                element: <ResetPasswordPage />,
              },
            ],
          },
          {
            path: 'auth/login',
            element: <LoginPage userImpl={impl} />, // TODO remade this
          },
          {
            element: <AuthenticatedLayout />,
            children: [
              {
                path: '',
                element: <HomePage />,
              },
              {
                path: 'reminders',
                element: <RemindersBrowsePage />,
              },
              {
                path: 'user-settings',
                element: <UserSettingsPage />,
              },
              {
                path: 'auditor-proposals',
                element: <AuditorProposalsBrowsePage />,
              },
              {
                path: 'auditor-proposals/:id',
                element: <AuditorProposalsDetailPage />,
              },
              {
                path: 'auditor-proposals/new',
                element: <AuditorProposalsCreatePage />,
              },
              {
                path: 'valid-registrations',
                element: <ValidAuditorRegistrationsBrowsePage />,
              },
              {
                path: 'valid-registrations/:id',
                element: <ValidAuditorRegistrationDetailPage />,
              },
              {
                path: 'auditor-competences',
                element: <AuditorCompetencesBrowsePage />,
              },
              {
                path: 'auditor-competences/:id',
                element: <AuditorCompetenceDetailPage />,
              },
              {
                path: 'auditor-competences/new',
                element: <AuditorCompetenceCreatePage />,
              },
              {
                path: 'auditor-visits',
                element: <AuditorVisitsBrowsePage />,
              },
              {
                path: 'auditor-visits/:id',
                element: <AuditorVisitDetailPage />,
              },
              // {
              //   path: 'client-offers',
              //   element: <ClientOffersBrowsePage />,
              // },
              // {
              //   path: 'client-offers/:id',
              //   element: <ClientOfferDetailPage />,
              // },
              {
                path: 'client-work-orders',
                element: <ClientWorkOrderBrowsePage />,
              },
              {
                path: 'client-work-order/:id',
                element: <ClientWorkOrderDetailPage />,
              },
              {
                element: <ClientGuard />,
                children: [
                  {
                    path: 'client-visits',
                    element: <ClientVisitsBrowsePage />,
                  },
                  {
                    path: 'client-visits/:id',
                    element: <ClientVisitDetailPage />,
                  },
                  // {
                  //   path: 'client-invoices',
                  //   element: <ClientInvoicesBrowsePage />,
                  // },
                  {
                    path: 'client-certificates',
                    element: <CertificateBrowsePage />,
                  },
                ],
              },
              {
                path: 'client-certificates/:id',
                element: <CertificateDetailPage />,
              },
              {
                path: 'audit-invitations',
                element: <AuditInvitationsBrowsePage />,
              },
              {
                path: 'audit-invitations/:id',
                element: <AuditInvitationDetailPage />,
              },
              {
                path: 'audit-visit-rep-review',
                element: <AuditorVisitRepReviewBrowsePage />,
              },
              {
                path: 'audit-visit-rep-review/:id',
                element: <AuditorVisitRepReviewDetailPage />,
              },
              {
                path: 'audit-survey',
                element: <AuditGradePage />,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <h1>Not found.</h1>,
  },
]);

i18nCore.changeLanguage(getInitialLanguage());

document.title = env.VITE_APP_TITLE || 'SIQ OSV Portal';

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <CoreWebWrapper options={options}>
      <SnackbarProvider maxSnack={1}>
        <QueryClientProvider client={queryClient}>
          <ConfirmProvider>
            <RouterProvider router={router} />
          </ConfirmProvider>
        </QueryClientProvider>
      </SnackbarProvider>
    </CoreWebWrapper>
  </React.StrictMode>
);
